import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Calculator, ArrowRight, X, Calendar as CalIcon, Car, Building, Home, Plane, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar } from './ui/calendar';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  vehicle_rental: Car,
  venue_rental: Building,
  accommodation: Home,
  airport_vip: Plane,
};

const UNIT_LABELS = {
  per_day: { fr: '/jour', en: '/day', ar: '/يوم' },
  per_night: { fr: '/nuit', en: '/night', ar: '/ليلة' },
  per_service: { fr: '/service', en: '/service', ar: '/خدمة' },
};

const SUB_LABELS = {
  vehicle_rental: { fr: 'Véhicules', en: 'Vehicles', ar: 'سيارات' },
  venue_rental: { fr: 'Salles', en: 'Venues', ar: 'قاعات' },
  accommodation: { fr: 'Hébergements', en: 'Accommodations', ar: 'إقامة' },
  airport_vip: { fr: 'VIP Aéroport', en: 'Airport VIP', ar: 'VIP المطار' },
};

const L = {
  title: { fr: 'Simulateur de Devis Instantané', en: 'Instant Quote Simulator', ar: 'محاكي العرض الفوري' },
  subtitle: {
    fr: 'Composez votre séjour sur-mesure et obtenez une estimation en temps réel.',
    en: 'Compose your bespoke experience and get a real-time estimate.',
    ar: 'صمم تجربتك الخاصة واحصل على تقدير فوري.',
  },
  duration: { fr: 'Durée (jours)', en: 'Duration (days)', ar: 'المدة (أيام)' },
  pickDate: { fr: 'Date souhaitée', en: 'Preferred date', ar: 'التاريخ المرغوب' },
  selectedItems: { fr: 'Éléments sélectionnés', en: 'Selected items', ar: 'العناصر المختارة' },
  addToQuote: { fr: 'Ajouter au devis', en: 'Add to quote', ar: 'أضف إلى العرض' },
  empty: { fr: 'Aucun élément sélectionné. Cliquez sur un item ci-dessous.', en: 'No item selected yet. Click an item below.', ar: 'لم يتم اختيار أي عنصر بعد.' },
  subtotal: { fr: 'Sous-total', en: 'Subtotal', ar: 'المجموع الفرعي' },
  serviceFee: { fr: 'Frais de service (5%)', en: 'Service fee (5%)', ar: 'رسوم الخدمة (5٪)' },
  vat: { fr: 'TVA (19.25%)', en: 'VAT (19.25%)', ar: 'ضريبة القيمة المضافة (19.25٪)' },
  total: { fr: 'Total estimatif', en: 'Estimated total', ar: 'المجموع التقديري' },
  convert: { fr: 'Transformer en réservation', en: 'Convert to booking', ar: 'تحويل إلى حجز' },
  calculating: { fr: 'Calcul en cours...', en: 'Calculating...', ar: 'جاري الحساب...' },
  quoteRef: { fr: 'Référence devis', en: 'Quote reference', ar: 'مرجع العرض' },
  pax: { fr: 'pers.', en: 'pax', ar: 'شخص' },
  quantity: { fr: 'Qté', en: 'Qty', ar: 'الكمية' },
  close: { fr: 'Fermer', en: 'Close', ar: 'إغلاق' },
  noCatalog: { fr: 'Ce service ne dispose pas de catalogue détaillé.', en: 'This service has no detailed catalog.', ar: 'هذه الخدمة لا تحتوي على كتالوج مفصل.' },
};

const QuoteSimulator = ({ service, language, dir, getLocalizedField }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [duration, setDuration] = useState(1);
  const [cart, setCart] = useState([]); // [{ subType, itemKey, label, unitPrice, qty, unit, meta }]
  const [quote, setQuote] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const locales = { fr, en: enUS, ar };
  const formatPrice = (p) => new Intl.NumberFormat('fr-FR').format(Math.round(p || 0));

  const subServices = service?.sub_services || [];

  // Auto-recalculate quote on cart/duration/date change (debounced)
  useEffect(() => {
    if (cart.length === 0) {
      setQuote(null);
      return;
    }
    const t = setTimeout(() => calculate(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, duration, selectedDate]);

  const calculate = async () => {
    setCalculating(true);
    try {
      const payload = {
        service_id: service.id,
        event_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
        duration_days: duration,
        items: cart.map((c) => ({
          sub_service_type: c.subType,
          item_key: c.itemKey,
          item_label: c.label,
          unit_price: c.unitPrice,
          quantity: c.qty,
          unit: c.unit,
        })),
      };
      const res = await axios.post(`${API}/quotes/calculate`, payload);
      setQuote(res.data);
    } catch (e) {
      toast.error('Erreur calcul devis');
    } finally {
      setCalculating(false);
    }
  };

  const addItem = (subType, unit, item, key, label, price) => {
    setCart((prev) => {
      const existing = prev.findIndex((c) => c.itemKey === key && c.subType === subType);
      if (existing >= 0) {
        const copy = [...prev];
        copy[existing] = { ...copy[existing], qty: copy[existing].qty + 1 };
        return copy;
      }
      return [...prev, { subType, itemKey: key, label, unitPrice: price, qty: 1, unit, meta: item }];
    });
    toast.success('Ajouté au devis', { duration: 1500 });
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => {
      const copy = [...prev];
      const newQty = copy[idx].qty + delta;
      if (newQty <= 0) {
        copy.splice(idx, 1);
      } else {
        copy[idx] = { ...copy[idx], qty: newQty };
      }
      return copy;
    });
  };

  const removeItem = (idx) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  };

  const convertToBooking = () => {
    if (!quote || cart.length === 0) {
      toast.error('Ajoutez au moins un élément');
      return;
    }
    navigate('/booking', {
      state: {
        fromQuote: {
          quoteId: quote.id,
          quoteReference: quote.reference,
          serviceId: service.id,
          eventDate: quote.event_date,
          durationDays: quote.duration_days,
          lines: quote.lines,
          total: quote.total,
        },
      },
    });
  };

  if (!service?.has_sub_services || subServices.length === 0) {
    return null;
  }

  return (
    <section className="bg-white border border-primary/15 shadow-[0_30px_60px_-30px_rgba(197,131,140,0.3)]" dir={dir}>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border-b border-primary/10">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-6 h-6 text-primary" />
          <h2 className="font-heading text-2xl md:text-3xl font-light text-text-primary">
            {L.title[language]}
          </h2>
        </div>
        <p className="font-body text-sm text-text-muted">{L.subtitle[language]}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* LEFT: Catalog picker */}
        <div className="lg:col-span-2 p-6 space-y-8 border-b lg:border-b-0 lg:border-r border-primary/10">
          {/* Date + duration controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block font-body text-xs text-text-muted mb-2">{L.pickDate[language]}</label>
              <button
                onClick={() => setShowCalendar((s) => !s)}
                className="w-full flex items-center justify-between border border-primary/20 p-3 bg-surface-light hover:border-primary/40 transition-colors"
                data-testid="quote-date-picker"
              >
                <span className="font-body text-sm text-text-primary">
                  {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: locales[language] || fr }) : '—'}
                </span>
                <CalIcon className="w-4 h-4 text-primary" />
              </button>
              {showCalendar && (
                <div className="absolute z-20 mt-1 bg-white border border-primary/20 shadow-xl">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setShowCalendar(false); }}
                    disabled={(date) => date < new Date()}
                    locale={locales[language] || fr}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block font-body text-xs text-text-muted mb-2">
                {L.duration[language]}: <span className="text-primary font-medium">{duration}</span>
              </label>
              <input
                type="range"
                min="1"
                max="30"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="w-full accent-primary"
                data-testid="quote-duration-slider"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>1</span><span>15</span><span>30</span>
              </div>
            </div>
          </div>

          {/* Catalog items by sub-service */}
          {subServices.map((sub) => {
            const Icon = ICONS[sub.type] || Building;
            const items = sub[
              sub.type === 'vehicle_rental' ? 'vehicles' :
              sub.type === 'venue_rental' ? 'venues' :
              sub.type === 'accommodation' ? 'accommodations' : 'services'
            ] || [];

            if (items.length === 0) return null;

            return (
              <div key={sub.type}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg font-medium text-text-primary">
                    {SUB_LABELS[sub.type][language]}
                  </h3>
                  <span className="text-xs text-text-muted">({items.length})</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map((item, i) => {
                    const key = `${sub.type}::${item.name || item.name_fr}::${i}`;
                    const label = item.name || item[`name_${language}`] || item.name_fr;
                    const inCart = cart.find((c) => c.itemKey === key);
                    return (
                      <div
                        key={key}
                        className={`border p-3 transition-all ${
                          inCart ? 'border-primary bg-primary/5' : 'border-primary/15 hover:border-primary/40'
                        }`}
                        data-testid={`quote-item-${i}`}
                      >
                        <div className="flex gap-3">
                          {item.images?.[0] && (
                            <img src={item.images[0]} alt={label} className="w-20 h-20 object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-heading text-sm font-medium text-text-primary truncate">{label}</p>
                            {(item.passengers || item.capacity) && (
                              <p className="font-body text-xs text-text-muted mt-0.5">
                                {item.passengers ? `${item.passengers} ${L.pax[language]}` : `${item.capacity} ${L.pax[language]}`}
                              </p>
                            )}
                            <p className="font-body text-sm text-primary mt-1">
                              {formatPrice(item.price)} <span className="text-xs">XAF{UNIT_LABELS[sub.price_unit]?.[language] || ''}</span>
                            </p>
                            <button
                              onClick={() => addItem(sub.type, sub.price_unit, item, key, label, item.price)}
                              className="mt-2 flex items-center gap-1 text-xs font-body text-primary hover:text-secondary transition-colors"
                              data-testid={`add-to-quote-${i}`}
                            >
                              {inCart ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              {inCart ? `Dans le devis (${inCart.qty})` : L.addToQuote[language]}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT: Live quote */}
        <div className="lg:col-span-1 p-6 bg-surface-light/50 lg:sticky lg:top-24 lg:self-start">
          <h3 className="font-heading text-lg font-medium text-text-primary mb-4">
            {L.selectedItems[language]}
          </h3>

          {cart.length === 0 ? (
            <div className="text-center py-8">
              <Calculator className="w-10 h-10 text-text-muted/50 mx-auto mb-3" />
              <p className="font-body text-sm text-text-muted">{L.empty[language]}</p>
            </div>
          ) : (
            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
              {cart.map((c, idx) => (
                <div key={idx} className="bg-white border border-primary/10 p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-body text-sm font-medium text-text-primary flex-1 line-clamp-2">
                      {c.label}
                    </p>
                    <button onClick={() => removeItem(idx)} className="text-red-500 hover:bg-red-50 p-0.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 border border-primary/20">
                      <button
                        onClick={() => updateQty(idx, -1)}
                        className="px-2 py-0.5 hover:bg-primary/5"
                        data-testid={`qty-minus-${idx}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 text-sm font-body">{c.qty}</span>
                      <button
                        onClick={() => updateQty(idx, 1)}
                        className="px-2 py-0.5 hover:bg-primary/5"
                        data-testid={`qty-plus-${idx}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="font-body text-sm text-primary">
                      {formatPrice(c.unitPrice * c.qty * (c.unit === 'per_service' ? 1 : duration))} XAF
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Totals */}
          {quote && cart.length > 0 && (
            <div className="space-y-2 pt-4 border-t border-primary/15 font-body text-sm" data-testid="quote-totals">
              <div className="flex justify-between text-text-muted">
                <span>{L.subtotal[language]}</span>
                <span>{formatPrice(quote.subtotal)} XAF</span>
              </div>
              <div className="flex justify-between text-text-muted">
                <span>{L.serviceFee[language]}</span>
                <span>{formatPrice(quote.service_fee)} XAF</span>
              </div>
              <div className="flex justify-between text-text-muted">
                <span>{L.vat[language]}</span>
                <span>{formatPrice(quote.vat)} XAF</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-primary/15">
                <span className="font-heading text-lg text-text-primary">{L.total[language]}</span>
                <span className="font-heading text-2xl text-primary" data-testid="quote-total">
                  {formatPrice(quote.total)} XAF
                </span>
              </div>
              {quote.reference && (
                <p className="text-xs text-text-muted pt-2">
                  {L.quoteRef[language]}: <code className="text-primary">{quote.reference}</code>
                </p>
              )}
            </div>
          )}

          {calculating && cart.length > 0 && (
            <p className="text-xs text-text-muted italic mt-2">{L.calculating[language]}</p>
          )}

          <button
            onClick={convertToBooking}
            disabled={cart.length === 0 || !quote}
            className="w-full mt-4 btn-primary flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="convert-to-booking-btn"
          >
            {L.convert[language]}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default QuoteSimulator;
